import {
	AfterContentInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ContentChildren,
	DestroyRef,
	EventEmitter,
	HostBinding,
	inject,
	Input,
	OnInit,
	Output,
	QueryList,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { filter, map, take, timeout } from 'rxjs/operators';

import { MovementEndKey, MovementEvent, ReachSliderDirective } from '~app-ui/cdk/slider';

import { CarouselSlideComponent } from '../carousel-slide/carousel-slide.component';
import { CarouselSlidesWrapperComponent } from '../carousel-slides-wrapper/carousel-slides-wrapper.component';

/**
 * CarouselContainer is the wrapper component that handles all the logic
 * regarding the carousel, having the logic to slide to any item.
 * Furhtermore, it handles the swipe.
 */
@Component({
	selector: 'carousel-container',
	templateUrl: './carousel-container.component.html',
	styleUrls: ['./carousel-container.component.scss'],
	host: { class: 'block overflow-hidden' },
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
})
export class CarouselContainerComponent<T extends string>
	extends ReachSliderDirective
	implements OnInit, AfterContentInit
{
	@Input()
	public carouselId!: string;

	private _lastManualSlide?: T;

	private _activeSlide!: T;

	@Input()
	public set activeSlide(activeSlide: T) {
		this._lastManualSlide = activeSlide;
		this.goToSlide(activeSlide);
	}

	public get activeSlide(): T {
		return this._activeSlide;
	}

	@Input()
	public slideTransitionTimeMs = 300;

	@Input()
	public maxTransitionTimeMultiplier = 5;

	@Input()
	public set canSwipe(canSwipe: boolean) {
		if (`${canSwipe}` === `true` || `${canSwipe}` === `false`) {
			const cleanValue = `${canSwipe}` === `true`;
			this.reachSlider = cleanValue;
		}
	}

	@Input()
	public override pixelsToStartSlide = 10;

	@HostBinding('style.--__extraLateralX.px')
	@Input()
	public override extraLateralPixels = 50;

	@Output()
	public activeSlideChange = new EventEmitter<T>();

	@HostBinding('style.--__currentSlideIndex')
	public get currentSlideIndexCssVar(): number {
		return this.currentIndex;
	}

	@HostBinding('style.--__transitionTime.ms')
	public get transitionTimeMsCssVar(): number {
		return this.manuallyMoving
			? 0
			: this.slideTransitionTimeMs *
					Math.min(this.maxTransitionTimeMultiplier, this.diffToNextSlide);
	}

	@HostBinding('style.--__maxSlidesIndex')
	public get maxSlideIndexCssVar(): number {
		return Math.max(0, this.slidesIds.length - 1);
	}

	/**
	 * POSITIVE: Swipe from right to left.
	 * NEGATIVE: Swipe from left to right.
	 */
	@HostBinding('style.--__manualSlidePercentageX')
	public get manualPercentageCssVar(): number {
		return this.manualPercentage;
	}

	public get childrenLength(): number {
		return this.slidesIds.length;
	}

	@ContentChildren(CarouselSlideComponent, { descendants: true })
	private slidesList!: QueryList<CarouselSlideComponent<T>>;

	@ContentChildren(CarouselSlidesWrapperComponent, { descendants: true })
	private wrapperList!: QueryList<CarouselSlidesWrapperComponent>;

	private manuallyMoving = false;
	private manualPercentage = 0;
	private diffToNextSlide = 1;

	public override get nativeElement(): HTMLElement {
		return this.elementRef.nativeElement;
	}

	private readonly destroyRef = inject(DestroyRef);
	private readonly cdRef = inject(ChangeDetectorRef, { skipSelf: true });

	ngAfterContentInit(): void {
		let firstTime = true;
		this.slidesList.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.handleChildrenChange(firstTime);
			firstTime = false;
		});

		this.slidesList.notifyOnChanges();
	}

	public goNext(): void {
		this.goToIndex(this.currentIndex + 1);
	}

	public goBack(): void {
		this.goToIndex(this.currentIndex - 1);
	}

	public goToIndex(index: number): void {
		const validIndex = Number.isInteger(index) ? index : 0;
		const cleanIndex = this.cleanIndex(validIndex);

		this.goToSlide(this.slidesIds[cleanIndex]);
	}

	public goToSlide(id: T): void {
		const validId = this.slidesIds.includes(id) ? id : this.slidesIds[0];

		const currentIdx = this.currentIndex;
		const targetIdx = this.slidesIds.indexOf(validId);
		this.diffToNextSlide = Math.abs(targetIdx - currentIdx);

		this.currentIndex = targetIdx;
		this._activeSlide = validId;

		this.updateUI();

		this.activeSlideChange.emit(this.activeSlide);
	}

	public updateUI(): void {
		this.cdRef.detectChanges();
	}

	protected get slidesIds(): T[] {
		return (this.slidesList?.toArray?.() || []).map((c) => c.slideId);
	}

	protected cleanIndex(rawIndex: number): number {
		return Math.max(0, Math.min(rawIndex, this.slidesIds.length - 1));
	}

	private handleChildrenChange(firstTime = false): void {
		const children = this.slidesList.toArray();

		children.forEach((c) => {
			c.containerId = this.carouselId;
		});

		this.goToSlide(firstTime ? this._lastManualSlide || this.activeSlide : this.activeSlide);

		this.indexCount = children.length;

		this.updateUI();
	}

	protected override handleMovement(event: MovementEvent): void {
		super.handleMovement(event);

		this.manuallyMoving = true;
		this.manualPercentage = event.percentageX;
		this.updateUI();
	}

	protected override async movementFinished(): Promise<MovementEndKey> {
		const direction = await super.movementFinished();

		await this.ngZone.run(async () => {
			this.animating = true;

			await this.handleMovementFinishSign(direction);

			this.animating = false;
			this.manuallyMoving = false;
			this.diffToNextSlide = 1;
			this.updateUI();
		});

		return direction;
	}

	private async handleMovementFinishSign(direction: MovementEndKey): Promise<void> {
		return await new Promise<void>((res) => {
			this.manuallyMoving = false;
			this.updateUI();

			requestAnimationFrame(async () => {
				switch (direction) {
					case MovementEndKey.SWIPED_LEFT_TO_RIGHT:
						await this.handleMovementToPrev();
						break;
					case MovementEndKey.SWIPED_RIGHT_TO_LEFT:
						await this.handleMovementToNext();
						break;
					default:
						await this.handleMovementToCurrent();
						break;
				}

				res();
			});
		});
	}

	private async handleMovementToPrev(): Promise<void> {
		const idx = this.currentIndex;
		if (idx === 0) {
			return await this.handleMovementToCurrent();
		}

		if (this.manualPercentage !== -1) {
			this.diffToNextSlide = 1 - Math.abs(this.manualPercentage);
			this.manualPercentage = -1;
			this.updateUI();

			await this.sliderFinishedTransition();
		}

		this.manualPercentage = 0;
		this.goBack();
	}

	private async handleMovementToNext(): Promise<void> {
		const idx = this.currentIndex;
		if (idx === this.slidesIds.length - 1) {
			return await this.handleMovementToCurrent();
		}

		if (this.manualPercentage !== 1) {
			this.diffToNextSlide = 1 - this.manualPercentage;
			this.manualPercentage = 1;
			this.updateUI();

			await this.sliderFinishedTransition();
		}

		this.manualPercentage = 0;
		this.goNext();
	}

	private async handleMovementToCurrent(): Promise<void> {
		if (this.manualPercentage !== 0) {
			this.diffToNextSlide = Math.abs(this.manualPercentage);
			this.manualPercentage = 0;
			this.updateUI();

			await this.sliderFinishedTransition();
		}

		this.manualPercentage = 0;
		this.updateUI();
	}

	private async sliderFinishedTransition(): Promise<void> {
		return await new Promise<void>((resolve) => {
			const slider = this.wrapperList.toArray()[0]?.slider;

			if (!slider) {
				resolve();
			}

			fromEvent(slider, 'transitionend')
				.pipe(
					map((e) => e as TransitionEvent),
					filter((e) => {
						return e.propertyName === 'transform' && e.target === slider;
					}),
					take(1),
					timeout(this.transitionTimeMsCssVar + 50)
				)
				.subscribe(
					() => {
						resolve();
					},
					() => {
						resolve();
					}
				);
		}).finally(() => {
			this.updateUI();
		});
	}
}
